// src/components/EditBookingModal.js
import React, { useState, useEffect, useRef } from "react";
import Modal from 'react-modal';
import { asTitle } from "../shared/utils/string";
import TextField from '@mui/material/TextField';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { StaticTimePicker } from '@mui/x-date-pickers/StaticTimePicker';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { CiClock2, CiCalendar } from "react-icons/ci";
import BookingService from "../services/booking.service";
import ProductService from "../services/product.service";
import CategoryService from "../services/category.service";
import UserService from "../services/user.service";
import HotelService from "../services/hotel.service";
import CalendarManagementService from '../services/calendar_management.service';
import Select from 'react-select';
import cantons from "../shared/data/cantons.json";
import { useNotifications } from '../context/NotificationProvider';
import { formatTimeTo24Hour } from "../shared/utils/string";
import { Map } from "../components/google_map";
import axios from "../api/axios";
import useAuth from "../hooks/useAuth";
import Autocomplete from 'react-google-autocomplete';
import { useLoadScript } from '@react-google-maps/api';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { PhoneInput } from 'react-international-phone';
import { useTranslation } from 'react-i18next';
import { useTranslatableData } from '../hooks/useTranslatable';
import { useLoading } from '../context/LoadingProvider';
import AsyncSelect from 'react-select/async';
import {
    addAdminZoneOverride,
    classifyAdminZoneEligibility,
    classifySelectedZoneCoverage,
    getFlyproId,
    getFlyproLabel,
} from '../shared/utils/adminZoneOverride';

Modal.setAppElement("#root");

const customStyles = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        position: 'absolute',
        boxshadow: '0 0 10px rgba(0, 0, 0, 0.5)',
        overflowX: 'hidden',
        scrollbarWidth: 'none',
        height: '680px',
    },
};

const customSelectStyles = {
    control: (styles) => ({
        ...styles,
        width: '100%',
        height: '60px',
        padding: '0 15px',
        fontSize: '14px',
        lineHeight: '60px',
        color: '#393D44',
        border: '0',
        borderRadius: '20px',
        background: '#fff',
    }),
    indicatorSeparator: () => ({ display: 'none' }),
};

export default function EditBookingModal({ open, onClose, booking, onSuccess, api }) {
    const { t, i18n } = useTranslation(['booking', 'customer', 'alerts']);
    const notify = useNotifications();
    const { auth } = useAuth();
    const userRoles = auth?.roles;
    const isPrivileged = userRoles?.some(role => role.name === "ROLE_ADMIN");
    const loader = useLoading();

    // --- Form state ---
    const [formData, setFormData] = useState({
        category_id: [],
        subcategory_id: [],
        products: [],
        duration: "",
        date: "",
        date_only: "",
        timeBooking: "",
        description: "",
        message: "",
        postcode: "",
        therapist_id: [],
        therapists: [],
        client_id: "",
        address_id: "",
        status: "",
        zip_code: "",
        state: "",
        state_code: "",
        street: "",
        street_number: "",
        city: "",
        country: "Switzerland",
        country_code: "CH",
        latitude: null,
        longitude: null,
        hotel_id: null,
        total_fare: 0,
        subtotal: 0,
        offline_payment: true,
        room: "",
    });

    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [productList, setProductList] = useState([]);
    const [flypros, setFlypros] = useState([]);
    const [flyprosStatus, setFlyprosStatus] = useState('idle');
    const [flyprosLigit, setFlyprosLigit] = useState(null);
    const [flyprosZoneOverrideLigit, setFlyprosZoneOverrideLigit] = useState(null);
    const [flyproEligibilityStatus, setFlyproEligibilityStatus] = useState('idle');
    const flyproEligibilityRequestRef = useRef(0);
    const [customers, setCustomers] = useState([]);
    const [hotels, setHotels] = useState([]);

    // Translated versions for display
    const translatedCategories = useTranslatableData(categories, ['title']);
    const translatedSubCategories = useTranslatableData(subCategories, ['title']);
    const translatedProductList = useTranslatableData(productList, ['name']);

    const [isAddressUpdate, setIsAddressUpdate] = useState(false);
    const [coordinates, setCoordinates] = useState({ lat: 46.356, lng: 6.5641 });
    const [selectedState, setSelectedState] = useState(null);
    const [showMap, setShowMap] = useState(false);
    const [selectedFlyproOption, setSelectedFlyproOption] = useState([]);
    const [selectedProductOptions, setSelectedProductOptions] = useState([]);
    const [productQuantities, setProductQuantities] = useState({});
    const [selectedHotel, setSelectedHotel] = useState(null);
    const [alertMessage, setAlertMessage] = useState('');
    const [isZipCodeVisible, setIsZipCodeVisible] = useState(false);
    const [showRefundChoice, setShowRefundChoice] = useState(false);
    const [pendingPayload, setPendingPayload] = useState(null);
    const [oldFare, setOldFare] = useState(null);
    const [newTotal, setNewtotal] = useState(null);
    const [newFare, setNewFare] = useState(null);
    const [showZoneOverrideConfirm, setShowZoneOverrideConfirm] = useState(false);
    const [zoneOverrideFlypros, setZoneOverrideFlypros] = useState([]);
    const [isSubmittingUpdate, setIsSubmittingUpdate] = useState(false);
    
    //  state for initial category/subcategory 
    const [initialCategoryIds, setInitialCategoryIds] = useState([]);
    const [initialSubcategoryIds, setInitialSubcategoryIds] = useState([]);

    const loadCustomerOptions = async (inputValue, callback) => {
        try {
            if(!inputValue || inputValue.length < 3) {
                callback([]);
                return;
            }
            const response = await UserService.indexByRole(
                { with: 'primary_address', status: '!eq:BLOCKED', name: `contains:${inputValue}` },
                'ROLE_CLIENT',
                api
            );
            setCustomers(response);
            const options = response.map(customer => ({
                value: customer.id,
                label: `${customer.id} - ${customer.extra?.full_name}`
            }));
            callback(options);
        } catch (error) {
            console.error("Error loading customer options", error);
            callback([]);
        }
    };

    useEffect(() => {
        if (!booking) return;
        const initializeForm = async () => {
            const dt = new Date(booking.start_before);

            const formatter = new Intl.DateTimeFormat('en-GB', {
                timeZone: 'Europe/Zurich',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            });

            const parts = formatter.formatToParts(dt);
            const date_only = `${parts.find(p => p.type === 'year').value}-${parts.find(p => p.type === 'month').value}-${parts.find(p => p.type === 'day').value}`;
            const timeBooking = `${parts.find(p => p.type === 'hour').value}:${parts.find(p => p.type === 'minute').value}`;

            console.log(date_only);
            console.log(timeBooking);


            const productsArray = typeof booking.products === 'string'
                ? booking.products.split(',').filter(Boolean)
                : Array.isArray(booking.products) ? booking.products : [];

                console.log('Raw booking.products:', booking.products);
                console.log('productsArray:', productsArray);

            
            let fullProductsArray = [];
            if (productsArray.length > 0) {
                if (typeof productsArray[0] === 'object' && productsArray[0].category) {
                    
                    fullProductsArray = productsArray;
                } else {
                   
                    const productIds = productsArray.map(p => typeof p === 'object' ? p.id : p);
                    try {
                        const productPromises = productIds.map(id => 
                            ProductService.show(id, { with: 'category,category.parent' }, api)
                        );
                        fullProductsArray = await Promise.all(productPromises);
                    } catch (error) {
                        console.error('Error fetching product details:', error);
                        fullProductsArray = productsArray;
                    }
                }
            }

            const products = fullProductsArray.map(p => typeof p === 'object' ? p.id : p);
            const therapist_id = booking.therapists.map(t => t.id);
            const therapists = booking.therapists.map(t => t.id);
            
            
           const categoryIds = [];
            const subcategoryIds = [];

            fullProductsArray.forEach(p => {
                if (p?.category) {
                    if (p.category.parent_id || (p.category.parent && p.category.parent.id)) {
                        
                        subcategoryIds.push(p.category.id);
                       
                        categoryIds.push(p.category.parent_id || p.category.parent.id);
                    } else {
                        
                        categoryIds.push(p.category.id);
                    }
                }
            });

            const uniqueCategoryIds = Array.from(new Set(categoryIds.filter(Boolean)));
            const uniqueSubcategoryIds = Array.from(new Set(subcategoryIds.filter(Boolean)));
            
            // Set initial IDs for  filteredProductOptions
            setInitialCategoryIds(uniqueCategoryIds);
            setInitialSubcategoryIds(uniqueSubcategoryIds);
            
            setFormData({
                ...formData,
                category_id: uniqueCategoryIds,
                subcategory_id: uniqueSubcategoryIds,
                products,
                duration: booking.duration || "",
                date: booking.start_before,
                date_only,
                timeBooking,
                description: booking.description || "",
                message: booking.message || "",
                postcode: booking.address.zip_code,
                therapist_id,
                therapists,
                client_id: booking.client?.id,
                address_id: booking.address.id,
                status: booking.status,
                zip_code: booking.address.zip_code,
                state: booking.address.state,
                state_code: booking.address.state_code,
                street: booking.address.street,
                street_number: booking.address.street_number,
                city: booking.address.city,
                country: booking.address.country || "Switzerland",
                country_code: booking.address.country_code || "CH",
                latitude: booking.address.latitude,
                longitude: booking.address.longitude,
                hotel_id: booking.hotel_id,
                total_fare: booking.total_fare || 0,
                subtotal: booking.subtotal || 0,
                offline_payment: !booking.offline_payment ? false : true,
                room: booking.room_type,
            });

            //  fetch products for the detected categories/subcategories
            let prods = [];
            if (uniqueSubcategoryIds.length > 0) {
                const subProds = await Promise.all(
                    uniqueSubcategoryIds.map(id => ProductService.index({ category_id: id, status: 'ACTIVE', with: 'category,category.parent' }, api))
                );
                prods = subProds.flat();
            } else if (uniqueCategoryIds.length > 0) {
                const catProds = await Promise.all(
                    uniqueCategoryIds.map(id => ProductService.index({ category_id: id, status: 'ACTIVE', with: 'category,category.parent' }, api))
                );
                prods = catProds.flat();
            }
            setProductList(prods);
            
            
            if (booking.hotel_id) {
                
                setSelectedHotel(booking.hotel_id);

                //  Load all categories and filter by hotel
                const allCats = await CategoryService.index({ parent_id: null, status: 'ACTIVE', with: 'domain,domain.zones,domain.hotel' }, api);
                const hotelCategories = allCats.filter(c => c.domain?.hotel_id === booking.hotel_id);
                setCategories(hotelCategories);

                //  Load subcategories for the hotel's categories
                const subCategoryPromises = hotelCategories.map(cat => 
                    CategoryService.index({ parent_id: cat.id, status: 'ACTIVE' }, api)
                );
                const hotelSubCategories = (await Promise.all(subCategoryPromises)).flat();
                setSubCategories(hotelSubCategories);

                //  Load products for the hotel
                const hotelProducts = await ProductService.index({ hotel_id: booking.hotel_id, status: 'ACTIVE', with: 'category,category.parent' }, api);
                setProductList(hotelProducts);

            } else {
              

//  Load all parent categories
const allParentCategories = await CategoryService.index({ parent_id: null, status: 'ACTIVE', with: 'domain,domain.zones,domain.hotel' }, api);
const filteredCategories = allParentCategories.filter(cat => {
    return !cat.domain?.hotel_id;
});

let categoriesToSet = [...filteredCategories]
// let categoriesToSet = [...allParentCategories];
console.log('All parent categories:', allParentCategories);

// Only load subcategories for the booking's existing categories
let subCategoriesToSet = [];
if (uniqueCategoryIds.length > 0) {
    const subCategoryPromises = uniqueCategoryIds.map(catId => 
        CategoryService.index({ parent_id: catId, status: 'ACTIVE' }, api)
    );
    subCategoriesToSet = (await Promise.all(subCategoryPromises)).flat();
}

console.log('Subcategories for booking categories:', subCategoriesToSet);

// Handle missing categories
const missingCategoryIds = categoryIds.filter(
  id => !categoriesToSet.some(cat => cat.id === id)
);
if (missingCategoryIds.length) {
  const missingCats = await Promise.all(
    missingCategoryIds.map(id => CategoryService.get(id, {}, api)) 
  );
  categoriesToSet = [...categoriesToSet, ...missingCats];
}

// Handle missing subcategories
const missingSubcategoryIds = subcategoryIds.filter(
  id => !subCategoriesToSet.some(sub => sub.id === id)
);
if (missingSubcategoryIds.length) {
  const missingSubs = await Promise.all(
    missingSubcategoryIds.map(id => CategoryService.get(id, {}, api)) 
  );
  subCategoriesToSet = [...subCategoriesToSet, ...missingSubs];
}

setCategories(categoriesToSet);
setSubCategories(subCategoriesToSet);


            }


            setCoordinates({
                lat: booking.address.latitude,
                lng: booking.address.longitude
            });

            setSelectedFlyproOption(booking.therapists.map(t => ({ value: t.id, label: t.extra.full_name })));
            
            // Set the pre-selected products and their quantities
            if (fullProductsArray?.length) {
                const uniqueProductsMap = {};
                const quantities = {};

                fullProductsArray.forEach(p => {
                    const productId = typeof p === 'object' ? p.id : p;
                    quantities[productId] = (quantities[productId] || 0) + 1;

                    if (!uniqueProductsMap[productId]) {
                        // Find translated version
                        const translatedProduct = translatedProductList.find(tp => tp.id === p.id) || p;
                        uniqueProductsMap[productId] = {
                            value: productId,
                            label: `${translatedProduct.name} - ${translatedProduct.duration} min`,
                            duration: typeof p === 'object' ? p.duration : 0,
                            price: typeof p === 'object' ? p.price : 0
                        };
                    }
                });

                setSelectedProductOptions(Object.values(uniqueProductsMap));
                setProductQuantities(quantities);
            }
        }
        initializeForm();
    }, [booking]);

    useEffect(() => {
        if (!open) return;
        
        const loadInitialData = async () => {
            try {
                const promises = [
                    // UserService.indexByRole({ with: 'primary_address' }, 'ROLE_CLIENT', api),
                    HotelService.index({ with: 'address,addresses' }, api)
                ];

                // Only load categories for new bookings, as it's handled for existing bookings in the other useEffect
                if (!booking) {
                    promises.unshift(CategoryService.index({ parent_id: null, status: 'ACTIVE', with: 'domain,domain.zones,domain.hotel' }, api));
                }

                const results = await Promise.all(promises);
                
                if (!booking) {
                    const [cats, hotels] = results;
                    setCategories(cats);
                    // setCustomers(clients);
                    setHotels(hotels);
                } else {
                    const [hotels] = results;
                    // setCustomers(clients);
                    if (booking.client_id) {
                        const customersRes = await UserService.indexByRole({id: booking.client_id, with: 'primary_address' }, 'ROLE_CLIENT', api);
                        setCustomers(customersRes);
                    }
                    setHotels(hotels);
                }
            } catch (error) {
                notify.error(t('errorInitializingForm', { ns: 'booking' }));
            }
        };

        loadInitialData();
    }, [open, booking]);

    useEffect(() => {
  if (!open) {
    resetForm();
  }
}, [open, booking]);
    const handleChange = e => {
        setFormData(f => ({ ...f, [e.target.name]: e.target.value }));
    };

    const handleCategorySelectChange = async selected => {
        const ids = selected?.map(o => o.value) || [];
        setFormData(f => ({
            ...f,
            category_id: ids,
            subcategory_id: [], 
            products: [] 
        }));
        setSelectedProductOptions([]); 
        
        if (!ids.length) {
            setSubCategories([]);
            setProductList([]);
            return;
        }
        try {
            const subs = await Promise.all(ids.map(id => CategoryService.index({ parent_id: id, status: 'ACTIVE' }, api)));
            setSubCategories(subs.flat());
            // Also fetch products for the selected parent categories
            const prods = await Promise.all(ids.map(id => ProductService.index({ category_id: id, status: 'ACTIVE', with: 'category,category.parent' }, api)));
            setProductList(prods.flat());
        } catch (err) { console.error(err) }
    };

    const handleSubcategorySelectChange = async selected => {
        const ids = selected?.map(o => o.value) || [];
        setFormData(f => ({ 
            ...f, 
            subcategory_id: ids,
            products: [] 
        }));
        setSelectedProductOptions([]); 
        
        if (!ids.length) {
            // If subcategories are cleared, refetch products for the parent category
            const parentCategoryIds = formData.category_id;
            if(parentCategoryIds.length > 0) {
                const prods = await Promise.all(parentCategoryIds.map(id => ProductService.index({ category_id: id, status: 'ACTIVE', with: 'category,category.parent' }, api)));
                setProductList(prods.flat());
            } else {
                setProductList([]);
            }
            return;
        }
        try {
            const prods = await Promise.all(ids.map(id => ProductService.index({ category_id: id, status: 'ACTIVE', with: 'category,category.parent' }, api)));
            setProductList(prods.flat());
        } catch (err) { console.error(err) }
    };

    const recalculateBookingData = (selected, quantities) => {
        const totalFare = selected.reduce((sum, p) => sum + (p.price || 0) * (quantities[p.value] || 1), 0);
        const totalDuration = Math.max(...selected.map(p => p.duration || 0));
        const productIds = selected.flatMap(p => Array(quantities[p.value] || 1).fill(p.value));
        setFormData(f => ({
            ...f,
            products: productIds,
            total_fare: totalFare,
            subtotal: totalFare,
            duration: totalDuration
        }));
    };

    const handleProductSelectChange = selected => {
        const sel = selected || [];
        const newQty = { ...productQuantities };
        sel.forEach(o => { if (!newQty[o.value]) newQty[o.value] = 1 });
        setSelectedProductOptions(sel);
        setProductQuantities(newQty);
        recalculateBookingData(sel, newQty);
    };

    const incrementQuantity = id => {
        setProductQuantities(q => {
            const cur = q[id] || 1;
            const next = cur < 4 ? cur + 1 : cur;
            const nq = { ...q, [id]: next };
            setTimeout(() => recalculateBookingData(selectedProductOptions, nq), 0);
            return nq;
        });
    };
    const decrementQuantity = id => {
        setProductQuantities(q => {
            const cur = q[id] || 1;
            const next = cur > 1 ? cur - 1 : 0;
            const nq = { ...q };
            if (next > 0) nq[id] = next;
            else {
                delete nq[id];
                setSelectedProductOptions(selectedProductOptions.filter(o => o.value !== id));
            }
            setTimeout(() => recalculateBookingData(selectedProductOptions, nq), 0);
            return nq;
        });
    };

    const handleFlyproChange = async selected => {
        setAlertMessage('');
        if ((selected?.length || 0) > 2) return;
        setSelectedFlyproOption(selected || []);
        setFormData(f => ({ ...f, therapist_id: (selected || []).map(o => o.value), therapists: (selected || []).map(o => o.value) }));
        const therapist_id = booking.therapists.map(t => t.id);
        const response = await CalendarManagementService.index(
            {
                therapist_id: therapist_id,
                with: 'therapist.media,therapist.zones,calendars_managements_slots.slot'
            },
            api
        );
        ligitFlyproAlerts(selected);
    };

    const ligitFlyproAlerts = async (selected = null) => {
        const legitIds = (flyproOptionsLigit || []).map(opt => opt.value);
        const { latitude, longitude, category_id } = formData;

        // Check each non-legit therapist
        for (const therapistObj of selected || selectedFlyproOption) {
            if (!legitIds.includes(therapistObj.value)) {
                try {
                    const therapistId = therapistObj.value;

                    const response = await CalendarManagementService.index(
                        {
                            therapist_id: therapistId,
                            with: 'therapist.media,therapist.zones,therapist.categories,calendars_managements_slots.slot'
                        },
                        api
                    );
                    console.log('response calendrier :', response);


                    const { date_only, timeBooking } = formData;
                    const selectedTime = formatTimeTo24Hour(timeBooking);

                    const availability = checkTherapistAvailability(response, date_only, selectedTime);

                    const therapistData = response[0]?.therapist;
                    let locationMatch = false;
                    let locationMessage = '';
                    let categoryMatch = false;
                    let categoryMessage = '';

                    if (therapistData) {

                        const isZipInZone = (zipCode, zone) => {
                            if (!zone.zip_code) return false;

                            const zipPatterns = zone.zip_code.split(',');

                            for (const pattern of zipPatterns) {
                                if (pattern.includes('-')) {
                                    const [start, end] = pattern.split('-').map(Number);
                                    const numericZip = Number(zipCode);
                                    if (numericZip >= start && numericZip <= end) {
                                        return true;
                                    }
                                }
                                else if (pattern === zipCode) {
                                    return true;
                                }
                            }

                            return false;
                        };

                        if (formData.zip_code && therapistData.zones?.length > 0) {
                            locationMatch = therapistData.zones.some(zone =>
                                zone.is_active && isZipInZone(formData.zip_code, zone)
                            );

                            if (!locationMatch) {
                                if (therapistData.latitude && therapistData.longitude) {
                                    locationMatch = (
                                        Math.abs(therapistData.latitude - latitude) < 0.0001 &&
                                        Math.abs(therapistData.longitude - longitude) < 0.0001
                                    );
                                }
                            }
                        } else if (therapistData.latitude && therapistData.longitude) {
                            locationMatch = (
                                Math.abs(therapistData.latitude - latitude) < 0.0001 &&
                                Math.abs(therapistData.longitude - longitude) < 0.0001
                            );
                        }

                        locationMessage = locationMatch
                            ? t('alerts:alerts.location.serves')
                            : t('alerts:alerts.location.notServes');

                        if (Array.isArray(category_id) && category_id.length > 0 && therapistData.categories?.length > 0) {
                            categoryMatch = therapistData.categories.some(
                                cat => category_id.includes(cat.id) && cat.is_active
                            );

                            const matchingCategories = therapistData.categories
                                .filter(cat => category_id.includes(cat.id) && cat.is_active)
                                .map(cat => {
                                    const translatedCat = translatedCategories.find(tc => tc.id === cat.id) || cat;
                                    return translatedCat.title;
                                });

                            const availableCategories = therapistData.categories
                                .filter(cat => cat.is_active)
                                .map(cat => {
                                    const translatedCat = translatedCategories.find(tc => tc.id === cat.id) || cat;
                                    return translatedCat.title;
                                });

                            if (categoryMatch) {
                                categoryMessage = t('alerts:alerts.category.offersSelected', {
                                    categories: matchingCategories.join(', ')
                                });
                            } else {
                                categoryMessage = t('alerts:alerts.category.notOffersSelected', {
                                    categories: availableCategories.join(', ')
                                });
                            }
                        } else {
                            categoryMessage = t('alerts:alerts.category.noData');
                        }
                    } else {
                        locationMessage = t('alerts:alerts.location.noData');
                        categoryMessage = t('alerts:alerts.category.noData');
                    }

                    let message = '';
                    if (!availability.available) {
                        message = t('alerts:alerts.availability.unavailable', {
                            name: therapistObj.label,
                            reason: availability.reason
                        });
                    } else {
                        message = t('alerts:alerts.availability.available', {
                            name: therapistObj.label,
                            time: formData.timeBooking
                        });
                    }

                    message += ` ${locationMessage}, ${categoryMessage}`;

                    console.log(availability.available ? '✅' : '⚠️', message);
                    setAlertMessage(message);

                } catch (error) {
                    console.error('Error checking availability:', error);
                }
            }
        }
    };

    function asTitles(status) {
        return t(`alerts:alerts.statusCalendar.${status}`);
    }

    const checkTherapistAvailability = (calendarData, selectedDate, selectedTime) => {

        console.log('response selectedDate :', selectedDate);

        const selectedDateISO = new Date(selectedDate).toISOString().split('T')[0]; // e.g., "2025-04-14"

        // Step 1: Find calendar entries matching the selected date
        const matchingCalendars = calendarData.filter(entry => {
            const date = new Date(entry.date);
            date.setDate(date.getDate());
            const calendarDate = date.toISOString().split('T')[0];
            return calendarDate === selectedDateISO;
        });


        if (!matchingCalendars.length) {
            return { available: false, reason: t('alerts:alerts.availability.noCalendar') };
        }

        // Step 2: Collect all slots from matching calendars
        const allSlots = matchingCalendars.flatMap(entry => entry.calendars_managements_slots || []);

        console.log('response matchingCalendars :', matchingCalendars);
        console.log('response allSlots :', allSlots);

        if (!allSlots.length) {
            return { available: true, reason: t('alerts:alerts.availability.noSlotData') };
        }

        // Step 3: Find a slot that matches the time
        const matchingSlot = allSlots.find(slotItem => {
            const slot = slotItem.slot;
            if (!slot) return false;

            const { start_time, end_time } = slot;

            return selectedTime >= start_time && selectedTime < end_time;
        });

        console.log('response matchingSlot :', matchingSlot);


        const ALLOWED_STATUSES = ['OVER_TIME', 'AVAILABLE'];

        if (!matchingSlot) {
            return { available: false, reason: t('alerts:alerts.availability.outsideSlots', { time: selectedTime }) };
        }

        // Check if the slot belongs to the current booking being edited
        const isCurrentBookingSlot = matchingSlot.booking_id === booking.id;

        if (!ALLOWED_STATUSES.includes(matchingSlot.status) && !isCurrentBookingSlot) {
            if (matchingSlot.length > 1) {
                const availableSlots = matchingSlot
                    .filter(slotItem => {
                        const slot = slotItem.slot;
                        return slot && ALLOWED_STATUSES.includes(slotItem.status);
                    })
                    .map(slotItem => {
                        const slot = slotItem.slot;
                        return {
                            start: slot.start_time,
                            end: slot.end_time,
                            status: slotItem.status
                        };
                    });

                if (availableSlots.length > 0) {
                    // Find the closest slot to selectedTime
                    const closestSlot = availableSlots.reduce((closest, current) => {
                        const diffCurrent = Math.abs(parseTimeToMinutes(current.start) - parseTimeToMinutes(selectedTime));
                        const diffClosest = Math.abs(parseTimeToMinutes(closest.start) - parseTimeToMinutes(selectedTime));
                        return diffCurrent < diffClosest ? current : closest;
                    });

                    return {
                        available: false,
                        reason: t('alerts:alerts.availability.closestSlot', {
                            status: asTitle(matchingSlot.status),
                            time: selectedTime,
                            closestStart: closestSlot.start,
                            closestEnd: closestSlot.end,
                            closestStatus: asTitles(closestSlot.status)
                        })
                    };
                }
            } else {
                return { available: false, reason: t('alerts:alerts.availability.currentlyStatus', {
                            status: asTitles(matchingSlot.status)}) 
                        };
            }
        }

        return { available: true, reason: t('alerts:alerts.availability.therapistIsStatus', { status: asTitles(matchingSlot.status) }) };
    };

    const parseTimeToMinutes = (timeStr) => {
        const [hours, minutes] = timeStr.split(':').map(Number);
        return hours * 60 + minutes;
    };

    const handleCustomerChange = async opt => {
        const clientId = opt?.value;
        setFormData(f => ({ ...f, client_id: clientId }));
        const resp = await UserService.index({ id: clientId, with: 'primary_address' }, api);
        const cli = resp[0];
        if (cli?.primary_address && !formData.hotel_id) {
            const a = cli.primary_address;
            setFormData(f => ({
                ...f,
                zip_code: a.zip_code,
                state: a.state,
                state_code: a.state_code,
                street: a.street,
                street_number: a.street_number,
                city: a.city,
                latitude: a.latitude,
                longitude: a.longitude,
                address_id: a.id
            }));
            setCoordinates({ lat: a.latitude, lng: a.longitude });
        }
    };

    const handleHotelSelect = async opt => {
        if (!opt) {
            setSelectedHotel(null);
            const [categoriesRes] = await Promise.all([
                CategoryService.index({
                    parent_id: null,
                    status: 'ACTIVE',
                    with: 'domain,domain.zones,domain.hotel'
                }, api),
            ]);
            const filteredCategories = categoriesRes.filter(cat => {
                return !cat.domain?.hotel_id;
            });
            
            setCategories(filteredCategories);
            // setCategories(categoriesRes);

            setFormData(f => ({
                ...f,
                hotel_id: null,
                room: null, 
                category_id: "",
                subcategory_id: "",
                products: [null]
            }));
            setSelectedProductOptions([]);

            return;
        }

        const hotelId = opt.value;
        setSelectedHotel(hotelId);
        const hotel = hotels.find(h => h.id === hotelId);
        const address = hotel?.address;

        const [allCategories] = await Promise.all([
            CategoryService.index({
                parent_id: null,
                status: 'ACTIVE',
                with: 'domain,domain.zones,domain.hotel'
            }, api),
        ]);

        const hotelCategories = allCategories.filter(
            category => category?.domain?.hotel_id === hotelId
        );

        setCategories(hotelCategories);

        setFormData(f => {
            const updated = {
                ...f,
                hotel_id: hotelId,
                room: 'SPA_ROOM',
                address_id: address?.id || null,
                zip_code: address?.zip_code || "",
                state: address?.state || "",
                state_code: address?.state_code || "",
                street: address?.street || "",
                street_number: address?.street_number || "",
                city: address?.city || "",
                latitude: address?.latitude || "",
                longitude: address?.longitude || "",
                category_id: [], subcategory_id: [], products: []

            };

            return updated;
        });

        setCoordinates({
            lat: address?.latitude,
            lng: address?.longitude
        });

        try {
            const prods = await ProductService.index({ hotel_id: hotelId, with: 'category,category.parent' }, api);
            setProductList(prods);
        } catch (err) { notify.error("Error loading hotel products") }
    };

    const handlePlaceSelect = place => {
        if (!place?.address_components) return;
        const updated = {
            street: '', street_number: '', city: '',
            state: '', state_code: '', zip_code: '',
            country: 'Switzerland', country_code: 'CH',
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng()
        };
        place.address_components.forEach(c => {
            switch (c.types[0]) {
                case 'street_number': updated.street_number = c.long_name; break;
                case 'route': updated.street = c.long_name; break;
                case 'locality': updated.city = c.long_name; break;
                case 'postal_code': updated.zip_code = c.long_name; break;
                case 'administrative_area_level_1':
                    updated.state = c.long_name;
                    updated.state_code = c.short_name; break;
                default: break;
            }
        });
        setFormData(f => ({ ...f, ...updated }));
        setCoordinates({ lat: updated.latitude, lng: updated.longitude });
        setShowMap(true);
    };

    const fetchCoordinates = async () => {
        const { street_number, street, city, state, zip_code } = formData;
        const address = `${street_number} ${street}, ${city}, ${state} ${zip_code}`;
        const resp = await axios.get('https://maps.googleapis.com/maps/api/geocode/json', {
            params: { address, key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY }
        });
        if (resp.data.results.length) {
            const loc = resp.data.results[0].geometry.location;
            setFormData(f => ({ ...f, latitude: loc.lat, longitude: loc.lng }));
            setCoordinates({ lat: loc.lat, lng: loc.lng });
        }
    };

    const handleShowMap = async () => {
        await fetchCoordinates();
        setShowMap(true);
    };

    // useEffect(() => {
    //     const loadAvailability = async () => {
    //         try {
    //             const allFlypros = await UserService.indexByRole(
    //                 { with: 'primary_address' },
    //                 'ROLE_FLYPRO',
    //                 api
    //             );
    //             setFlypros(allFlypros);

    //             if (
    //                 formData.date_only &&
    //                 formData.timeBooking &&
    //                 formData.duration &&
    //                 formData.latitude != null &&
    //                 formData.longitude != null &&
    //                 formData.zip_code
    //             ) {
    //                 const avail = await CalendarManagementService.index(
    //                     {
    //                         date: formData.date_only,
    //                         time: formatTimeTo24Hour(formData.timeBooking),
    //                         duration: Number(formData.duration),
    //                         lat: formData.latitude,
    //                         lng: formData.longitude,
    //                         zip_code: formData.zip_code,
    //                         with: 'therapist.primary_address,therapist.media,therapist.zones,calendars_managements_slots.slot'
    //                     },
    //                     api
    //                 );
    //                 setFlyprosLigit(avail);
    //             }
    //         } catch (e) {
    //             console.error(e);
    //         }
    //     };

    //     if (open) loadAvailability();
    // }, [
    //     open
    // ]);

    const selectedFlyproIdsSignature = selectedFlyproOption
        .map(getFlyproId)
        .filter(Boolean)
        .join(',');
    const selectedProductIdsSignature = (formData.products || []).join(',');

    useEffect(() => {
        const shouldLoad =
            open &&
            formData.longitude &&
            formData.latitude &&
            formData.duration &&
            formData.date_only &&
            formData.timeBooking &&
            formData.state &&
            formData.zip_code &&
            formData.category_id;

        const fetchData = async () => {
            try {
                if (shouldLoad) {
                    console.log("Fetching legit therapists... and all");
                    await fetchAvailableTherapistsLigit();
                    await fetchAvailableTherapists();
                } else {
                    flyproEligibilityRequestRef.current += 1;
                    setFlyproEligibilityStatus('idle');
                    setFlyprosLigit(null);
                    setFlyprosZoneOverrideLigit(null);
                    console.log("Fetching all therapists...");
                    await fetchAvailableTherapists();
                }
            } catch (error) {
                console.error("Error loading therapists:", error);
            }
        };

        fetchData();
    }, [
        formData.longitude,
        formData.latitude,
        formData.duration,
        formData.date_only,
        formData.timeBooking,
        formData.state,
        formData.zip_code,
        formData.category_id,
        selectedFlyproIdsSignature,
        selectedProductIdsSignature,
    ]);

    const fetchAvailableTherapists = async () => {
        setFlyprosStatus('loading');
        try {
                const flypro = await UserService.indexByRole({
                    with: 'primary_address,zones,categories',
                    ...(!isPrivileged && { ['flypro_manager_id']: auth?.user?.id })
                }, 'ROLE_FLYPRO', api);
                const sortedFlypros = flypro.sort((a, b) =>
                (a.extra?.full_name || '').localeCompare(b.extra?.full_name || '', undefined, { sensitivity: 'base' })
            );
            setFlypros(sortedFlypros);
            setFlyprosStatus('success');
        } catch (error) {
            console.error('Error loading FlyPros:', error);
            setFlyprosStatus('error');
        }
    }

    const fetchAvailableTherapistsLigit = async () => {
        const requestId = flyproEligibilityRequestRef.current + 1;
        flyproEligibilityRequestRef.current = requestId;
        setFlyproEligibilityStatus('loading');

        try {
            const availabilityPayload = {
                hotel_id: formData.hotel_id || undefined,
                is_hotel: Boolean(formData.hotel_id),
                room_type: formData.hotel_id ? formData.room : undefined,
                booking_id: booking?.id,
                date: formData.date_only,
                time: formatTimeTo24Hour(formData.timeBooking),
                duration: Number(formData.duration),
                lat: formData.latitude,
                lng: formData.longitude,
                zip_code: formData.zip_code,
                products: formData.products || [],
                is_duo: selectedFlyproOption.length > 1,
            };

            const flyproLigitResponse = await UserService.fetchAvailableTherapists(
                availabilityPayload,
                null,
                api
            );
            const flyproLigit = Array.isArray(flyproLigitResponse)
                ? flyproLigitResponse
                : Object.values(flyproLigitResponse || {}).flat().filter(Boolean);
            const sortedFlyprosLigit = flyproLigit.sort((a, b) =>
                (a.extra?.full_name || '').localeCompare(b.extra?.full_name || '', undefined, { sensitivity: 'base' })
            );

            const missingSelectedIds = classifyAdminZoneEligibility(
                selectedFlyproOption,
                sortedFlyprosLigit,
                sortedFlyprosLigit
            ).invalid.map(getFlyproId).filter(Boolean).map(Number);

            let sortedFlyprosZoneOverrideLigit = sortedFlyprosLigit;
            if (missingSelectedIds.length) {
                const zoneOverrideLigitResponse = await UserService.fetchAvailableTherapists(
                    {
                        ...availabilityPayload,
                        zone_override_therapist_ids: missingSelectedIds,
                    },
                    null,
                    api
                );
                const zoneOverrideLigit = Array.isArray(zoneOverrideLigitResponse)
                    ? zoneOverrideLigitResponse
                    : Object.values(zoneOverrideLigitResponse || {}).flat().filter(Boolean);
                sortedFlyprosZoneOverrideLigit = zoneOverrideLigit.sort((a, b) =>
                    (a.extra?.full_name || '').localeCompare(b.extra?.full_name || '', undefined, { sensitivity: 'base' })
                );
            }

            if (flyproEligibilityRequestRef.current !== requestId) return;
            setFlyprosLigit(sortedFlyprosLigit);
            setFlyprosZoneOverrideLigit(sortedFlyprosZoneOverrideLigit);
            setFlyproEligibilityStatus('success');
        } catch (error) {
            if (flyproEligibilityRequestRef.current !== requestId) return;
            console.error('Error checking FlyPro zone eligibility:', error);
            setFlyprosLigit(null);
            setFlyprosZoneOverrideLigit(null);
            setFlyproEligibilityStatus('error');
        }
    }

    const categoryOptions = translatedCategories?.map(c => ({ value: c.id, label: `${c.domain?.hotel ? c.domain?.hotel?.name + ' - ' + c.title : c.title}` }));
    const subcategoryOptions = translatedSubCategories?.map(s => ({ value: s.id, label: s.title }));
    const productOptions = translatedProductList?.map(p => ({ value: p.id, label: p.name, duration: p.duration, price: p.price }));

    const flyproOptions = flypros?.map(f => ({ value: f.id, label: f.extra.full_name }));
    const flyproOptionsLigit = (flyprosLigit || []).map(row => ({ value: row.id, label: row.extra?.full_name }));
    const flyproOptionsZoneOverrideLigit = (flyprosZoneOverrideLigit || []).map(row => ({
        value: row.id,
        label: row.extra?.full_name,
    }));
    const customerOptions = customers?.map(c => ({
        value: c.id,
        label: c.extra?.full_name || `${c.firstname} ${c.lastname}`
    }));
    const hotelOptions = hotels?.map(h => ({ value: h.id, label: h.name }));

    const handleCoordinateChange = coords => {
        setFormData(f => ({ ...f, latitude: coords[0].lat, longitude: coords[0].lng }));
        setCoordinates({ lat: coords[0].lat, lng: coords[0].lng });
    };
    const resetForm = () => {
        setFormData({
            category_id: "",
            subcategory_id: "",
            products: [null],
            duration: "",
            date: "",
            date_only: "",
            timeBooking: "",
            description: "",
            message: "",
            postcode: "",
            therapist_id: [],
            therapists: [],
            client_id: "",
            address_id: "",
            status: "CREATED",
            zip_code: "",
            state: "",
            state_code: "",
            street: "",
            street_number: "",
            city: "",
            country: "Switzerland",
            country_code: "CH",
            latitude: null,
            longitude: null,
            hotel_id: auth.user.hotel[0]?.id,
            total_fare: 0,
            subtotal: 0,
            offline_payment: false,
            room: "",
        });
        setSelectedFlyproOption([]);
        setSubCategories([]);
        setProductList([]);
        setCoordinates({ lat: 46.356, lng: 6.5641 });
        setIsAddressUpdate(false);
        setSelectedProductOptions([]);
        setSelectedHotel(null);
        setAlertMessage('');
        setFlypros([]);
        setFlyprosStatus('idle');
        flyproEligibilityRequestRef.current += 1;
        setFlyprosLigit(null);
        setFlyprosZoneOverrideLigit(null);
        setFlyproEligibilityStatus('idle');
        setShowZoneOverrideConfirm(false);
        setZoneOverrideFlypros([]);
        setPendingPayload(null);
        // Reset initial category/subcategory IDs
        setInitialCategoryIds([]);
        setInitialSubcategoryIds([]);
    };

    const buildCleanUpdatePayload = (combined, durationMax) => {
        const {
            status,
            offline_payment,
            payment_link,
            transaction_id,
            payment_url,
            payment_iframe,
            payment_lightbox,
            ...editableFormData
        } = formData;

        return {
            ...editableFormData,
            date: combined,
            start_before: combined,
            latitude: coordinates.lat,
            room_type: formData.hotel_id ? formData.room : null,
            duration: durationMax ?? formData.duration,
            longitude: coordinates.lng,
            device_type: `${navigator.userAgentData?.platform} - ${navigator.userAgentData?.brands?.[1]?.brand}`,
            device_version: navigator.userAgentData?.brands?.[1]?.version
        };
    };

    const submitUpdatePayload = async payload => {
        const oldTotal = booking.total_fare;
        const newTotal = payload.total_fare;

        if (newTotal < oldTotal) {
            setOldFare(oldTotal);
            setNewFare(newTotal);
            setPendingPayload(payload);
            return setShowRefundChoice(true);
        }

        if (isSubmittingUpdate) return;
        setIsSubmittingUpdate(true);
        try {
            await BookingService.update(booking.id, payload, api);
            onSuccess(); onClose();
        } catch (err) {
            console.error(err);
            notify.error(err?.response?.data?.message || t('alerts:error'));
        } finally {
            setIsSubmittingUpdate(false);
        }
    };

    const handleFormSubmit = async e => {
        e.preventDefault();
        let durationMax = null;

        if (selectedFlyproOption.length == 1) {

            console.log({ selectedFlyproOption })
            durationMax = formData.products.reduce((total, productId) => {
                const product = productList.find(p => p.id === productId);
                return total + (Number(product?.duration) || 0);
            }, 0);

            console.log({ durationMax })
        } else {
            durationMax = formData.products.reduce((max, productId) => {
                const product = productList.find(p => p.id === productId);
                return Math.max(max, Number(product?.duration) || 0);
            }, 0);
        }
        if (isAddressUpdate || (formData.zip_code && !formData.latitude)) {
            try { await fetchCoordinates(); setIsAddressUpdate(false); }
            catch { notify.error(t('errorValidatingAddress')); return; }
        }
        const time = formatTimeTo24Hour(formData.timeBooking);
        const combined = `${formData.date_only} ${time}`;
        const payload = buildCleanUpdatePayload(combined, durationMax);
        if (flyproEligibilityStatus === 'loading' || flyprosStatus === 'loading') {
            notify.error(t('zoneOverride.checking', { ns: 'booking' }));
            return;
        }

        const zoneCoverage = classifySelectedZoneCoverage(
            selectedFlyproOption,
            flypros,
            formData.zip_code
        );

        if (
            flyprosStatus === 'success'
            && zoneCoverage.unknown.length === 0
            && zoneCoverage.outsideZone.length
        ) {
            if (flyproEligibilityStatus !== 'success') {
                notify.error(t('zoneOverride.checkFailed', { ns: 'booking' }));
                return;
            }

            const { outsideZone, invalid } = classifyAdminZoneEligibility(
                zoneCoverage.outsideZone,
                [],
                flyproOptionsZoneOverrideLigit
            );

            if (invalid.length) {
                notify.error(t('zoneOverride.notEligible', { ns: 'booking' }));
                return;
            }

            if (outsideZone.length) {
                setZoneOverrideFlypros(outsideZone);
                setPendingPayload(payload);
                return setShowZoneOverrideConfirm(true);
            }
        }

        await submitUpdatePayload(payload);
    };

    async function handleZoneOverrideConfirm() {
        if (!pendingPayload) return;

        const payload = addAdminZoneOverride(pendingPayload, zoneOverrideFlypros);

        setShowZoneOverrideConfirm(false);
        await submitUpdatePayload(payload);
    }

    function handleZoneOverrideCancel() {
        setShowZoneOverrideConfirm(false);
        setZoneOverrideFlypros([]);
        setPendingPayload(null);
    }

    async function submitRefundDecision(decisionPayload) {
        if (!pendingPayload || isSubmittingUpdate) return;

        setShowRefundChoice(false);
        setIsSubmittingUpdate(true);
        loader.start();
        try {
            await BookingService.update(
                booking.id,
                { ...pendingPayload, ...decisionPayload },
                api
            );
            onSuccess();
            onClose();
        } catch (err) {
            console.error(err);
            notify.error(err?.response?.data?.message || t('alerts:error'));
        } finally {
            loader.stop();
            setIsSubmittingUpdate(false);
        }
    }

    async function handleRefundConfirm() {
        await submitRefundDecision({ refund_type: 'REFUND' });
    }

    async function handleIssueCreditConfirm() {
        await submitRefundDecision({ refund_type: 'FLATT_OFF' });
    }

    async function handleNoRefundConfirm() {
        await submitRefundDecision({ no_refund_edit: true });
    }

    const filteredFlyproOptions = flyproOptionsLigit.length > 0
        ? flyproOptions.filter(opt => !flyproOptionsLigit.some(ligitOpt => ligitOpt.value === opt.value))
        : flyproOptions;

    const mergedOptions = flyproOptionsLigit.length > 0
        ? [
            {
                label: <div style={{ textAlign: 'center', color: '#888' }}>────────── FlyPros disponibles ──────────</div>,
                value: '',
                isDisabled: true,
            },
            ...flyproOptionsLigit,
            {
                label: <div style={{ textAlign: 'center', color: '#888' }}>────────── Tous les FlyPros ──────────</div>,
                value: '',
                isDisabled: true,
            },
            ...filteredFlyproOptions,
        ]
        : [...flyproOptions];


    if (!open || !booking) return null;

    return (
        <>
            <Modal
                isOpen={open}
                onRequestClose={onClose}
                style={customStyles}
                contentLabel={t('booking:editModal.title')}
            >
                <button className="close-button" onClick={onClose}>
                    <span className="close-icon">✖</span>
                </button>

                <h2>{t('booking:editModal.heading', { id: booking.id })}</h2>
                <p>{t('booking:editModal.subheading')}</p>

                <div className="form-inscription">
                    <form onSubmit={handleFormSubmit}>
                        {/* Customer */}
                        <div className="form-text">
                            <label>{t('customer')} *</label>
                            <AsyncSelect
                                cacheOptions
                                defaultOptions={customerOptions}
                                defaultInputValue={customerOptions.find(o => o.value === formData.client_id)}
                                loadOptions={loadCustomerOptions}
                                value={customerOptions.find(o => o.value === formData.client_id)}
                                onChange={handleCustomerChange}
                                options={customerOptions}
                                styles={customSelectStyles}
                                isClearable
                                isSearchable
                                required={!booking.hotel}
                                placeholder={t('selectCustomer')}
                            />
                        </div>

                        {/* Address Autocomplete */}
                        <div className="flex-row gap-10">
                            <div className="form-text">
                                <label>{t('enterAddress')}</label>
                                <Autocomplete
                                    apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
                                    onPlaceSelected={handlePlaceSelect}
                                    options={{ types: ['address'], componentRestrictions: { country: 'CH' } }}
                                    defaultValue={
                                        formData.street
                                            ? `${formData.street_number} ${formData.street}, ${formData.city}, ${formData.state} ${formData.zip_code}`
                                            : ""
                                    }
                                    className={`address-input ${isZipCodeVisible ? 'compact' : ''}`}
                                    style={{ height: 60, padding: '0 15px', border: 0, borderRadius: 20 }}
                                />
                            </div>
                            {isZipCodeVisible && (
                                <div className="form-text zip-code-input">
                                    <label>ZIP Code *</label>
                                    <input
                                        type="text"
                                        name="zip_code"
                                        value={formData.zip_code}
                                        onChange={e => setFormData(f => ({ ...f, zip_code: e.target.value }))}
                                        required
                                    />
                                </div>
                            )}
                            <div className="form-group icon-top-margin">
                                {formData.zip_code && (
                                    <img
                                        src="/assets/images/map-icon.svg"
                                        alt="Map Icon"
                                        onClick={handleShowMap}
                                        className="clickable"
                                    />
                                )}
                            </div>
                        </div>

                        {/* Map */}
                        {formData.latitude && showMap && (
                            <Map
                                coordinates={[coordinates]}
                                mode="edit"
                                containerStyle={{ width: '100%', height: 400 }}
                                mapOptions={{
                                    disableDefaultUI: true,
                                    zoomControl: true,
                                    mapTypeControl: false,
                                    fullscreenControl: true,
                                    streetViewControl: false,
                                }}
                                onCoordinateChange={handleCoordinateChange}
                            />
                        )}

                        {/* Hotel */}
                        <div className="form-text">
                            <label>{t('hotel')}</label>
                            <Select
                                value={hotelOptions.find(o => o.value === selectedHotel)}
                                onChange={handleHotelSelect}
                                options={hotelOptions}
                                styles={customSelectStyles}
                                isClearable
                                placeholder={t('selectHotel')}
                            />
                        </div>

                        {selectedHotel ? (
                            <div className="champs-notif" style={{ paddingBottom: '20px' }}>
                                <input
                                    type="checkbox"
                                    checked={formData.room === 'SPA_ROOM'}
                                    onChange={() =>
                                        setFormData(prev => ({
                                            ...prev,
                                            room: prev.room === 'SPA_ROOM' ? null : 'SPA_ROOM'
                                        }))
                                    }
                                />
                                <label>{t('service:salle_massage')}</label>

                                <input
                                    type="checkbox"
                                    checked={formData.room === 'HOTEL_ROOM'}
                                    onChange={() =>
                                        setFormData(prev => ({
                                            ...prev,
                                            room: prev.room === 'HOTEL_ROOM' ? null : 'HOTEL_ROOM'
                                        }))
                                    }
                                />
                                <label>{t('service:chambre_massage')}</label>
                            </div>

                        ) : null}
                        <>
                            <div className="form-text">
                                <label>{t('category')} *</label>
                                <Select
                                    value={categoryOptions.filter(o => formData.category_id.includes(o.value))}
                                    onChange={handleCategorySelectChange}
                                    options={categoryOptions}
                                    styles={customSelectStyles}
                                    isMulti
                                    isClearable
                                    required
                                    placeholder={t('selectCategory')}
                                />
                            </div>
                            <div className="form-text">
                                <label>{t('subcategory')}</label>
                                <Select
                                    value={subcategoryOptions.filter(o => formData.subcategory_id.includes(o.value))}
                                    onChange={handleSubcategorySelectChange}
                                    options={subcategoryOptions}
                                    styles={customSelectStyles}
                                    isMulti
                                    isClearable
                                    placeholder={t('selectSubcategory')}
                                />
                            </div>
                        </>

                        {/* Products */}
                        <div className="form-text">
                            <label>{t('product')} *</label>
                            <Select
                                value={selectedProductOptions}
                                onChange={handleProductSelectChange}
                                options={selectedHotel ? translatedProductList.map(p => ({
                                    value: p.id, label: `${p.name} - ${p.duration} min`, duration: p.duration, price: p.price
                                })) : productOptions}
                                styles={customSelectStyles}
                                isMulti
                                isClearable
                                required
                                placeholder={t('selectProducts')}
                            />
                            {selectedProductOptions.length > 0 && (
                                <div className="selected-products">
                                    {selectedProductOptions.map(p => {
                                        const qty = productQuantities[p.value] || 1;
                                        return (
                                            <div className="product-row" key={p.value}>
                                                <div className="quantity-controls">
                                                    <button type="button" onClick={() => decrementQuantity(p.value)}>-</button>
                                                    <span>{qty}</span>
                                                    <button type="button" onClick={() => incrementQuantity(p.value)}>+</button>
                                                </div>
                                                <div className="summary">
                                                    {qty}× {p.label}
                                                </div>
                                            </div>
                                        )
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Hidden duration */}
                        <input type="hidden" name="duration" value={formData.duration} />

                        {/* Date & Time */}
                        <div className="dates">
                            <div className="form-text">
                                <label>{t('dateOfBooking')} *</label>
                                <input
                                    type="date"
                                    name="date_only"
                                    value={formData.date_only}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                            <div className="form-text">
                                <label>{t('timeOfBooking')} *</label>
                                <input
                                    type="time"
                                    name="timeBooking"
                                    value={formData.timeBooking}
                                    onChange={handleChange}
                                    required
                                />
                            </div>
                        </div>

                        {/* Therapists */}
                        <div className="form-text">
                            <label>{t('flypro')} *</label>
                            <Select
                                value={selectedFlyproOption}
                                onChange={handleFlyproChange}
                                options={mergedOptions}
                                styles={customSelectStyles}
                                isMulti
                                isClearable
                                required
                                placeholder={t('selectFlypro')}
                            />
                        </div>
                        {alertMessage && <div className="alert">{alertMessage}</div>}

                        {/* Description */}
                        <div className="form-text topMargArea">
                            <label>{t('description')} {t('notVisible')}</label>
                            <textarea
                                className="formTextArea"
                                placeholder={t('write_message_admin')}
                                name="description"
                                value={formData.description}
                                onChange={e =>
                                    setFormData(f => ({ ...f, description: e.target.value }))
                                }
                            />
                        </div>

                        {/* Comment */}
                        <div className="form-text topMargArea">
                            <label>{t('comment')} {t('visible')}</label>
                            <textarea
                                className="formTextArea"
                                placeholder={t('write_message_client')}
                                name="message"
                                value={formData.message}
                                onChange={e =>
                                    setFormData(f => ({ ...f, message: e.target.value }))
                                }
                            />
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={
                                isSubmittingUpdate
                                || flyproEligibilityStatus === 'loading'
                                || flyprosStatus === 'loading'
                            }
                        >
                            {t('booking:editModal.saveButton')}
                        </button>
                    </form>
                </div>
            </Modal>

            {showRefundChoice && (
                <Modal isOpen onRequestClose={() => setShowRefundChoice(false)} style={{
                    content: {
                        width: "90%",
                        maxWidth: "480px",
                        maxHeight: "420px",
                        margin: "auto",
                        padding: "50px",
                        borderRadius: "12px",
                        textAlign: "center"
                    }
                }}>
                    <h2>Prix réduit</h2>
                    <p>
                        L’ancien tarif était <strong>{oldFare} CHF</strong>,<br />
                        le nouveau est <strong>{newFare} CHF</strong>.<br />
                        Voulez-vous rembourser la différence ou émettre un avoir ?
                    </p>
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                        <button
                            onClick={handleRefundConfirm}
                            className="btn btn-primary"
                            disabled={isSubmittingUpdate}
                        >
                            Rembourser
                        </button>
                        <button
                            onClick={handleIssueCreditConfirm}
                            className="btn btn-secondary"
                            disabled={isSubmittingUpdate}
                        >
                            Émettre un avoir
                        </button>
                        <button
                            onClick={handleNoRefundConfirm}
                            className="btn btn-secondary"
                            disabled={isSubmittingUpdate}
                        >
                            Non Rembourser
                        </button>
                    </div>
                    <button onClick={() => setShowRefundChoice(false)} className="btn btn-light">
                            Annuler
                    </button>
                </Modal>
            )}
            {showZoneOverrideConfirm && (
                <Modal
                    isOpen
                    onRequestClose={handleZoneOverrideCancel}
                    className="admin-zone-override-modal"
                    overlayClassName="admin-zone-override-overlay"
                >
                    <h3>{t('zoneOverride.title', { ns: 'booking' })}</h3>
                    <p>
                        {t('zoneOverride.message', {
                            ns: 'booking',
                            flypros: zoneOverrideFlypros.map(getFlyproLabel).join(', '),
                        })}
                    </p>
                    <div className="admin-zone-override-actions">
                        <button
                            onClick={handleZoneOverrideConfirm}
                            className="btn btn-primary"
                            disabled={isSubmittingUpdate}
                        >
                            {t('zoneOverride.confirm', { ns: 'booking' })}
                        </button>
                        <button
                            onClick={handleZoneOverrideCancel}
                            className="btn btn-secondary"
                            disabled={isSubmittingUpdate}
                        >
                            {t('zoneOverride.cancel', { ns: 'booking' })}
                        </button>
                    </div>
                </Modal>
            )}
        </>
    );
}
